import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield } from "lucide-react";
import { Footer } from "@/components/Footer";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — LendyTrack" },
      {
        name: "description",
        content:
          "Privacy Policy for LendyTrack, a consumer loan intelligence product of Oasis Media Group LLC.",
      },
      { property: "og:title", content: "Privacy Policy — LendyTrack" },
      {
        property: "og:description",
        content: "Read how LendyTrack collects, uses, and protects your information.",
      },
      { property: "og:url", content: "/privacy" },
    ],
    links: [{ rel: "canonical", href: "/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gradient-surface">
      <header className="border-b border-border bg-card/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-hero text-primary-foreground shadow-soft">
              <Shield className="h-5 w-5" />
            </div>
            <span className="font-display text-lg font-semibold">LendyTrack</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-14">
        <h1 className="font-display text-4xl font-semibold tracking-tight">Privacy Policy</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: June 22, 2026</p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-foreground">
          <p className="text-muted-foreground">
            This page is maintained by LendyTrack to answer common privacy and security questions about our service.
            It is app-owned content and is not an independent certification, audit report, or legal opinion.
          </p>

          <section>
            <h2 className="font-display text-xl font-semibold">1. Information We Collect</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
              <li>
                <strong>Account information:</strong> name, email address, and authentication details when you sign up.
              </li>
              <li>
                <strong>Usage information:</strong> lender searches, loan inputs, feature usage, and AI coach messages.
              </li>
              <li>
                <strong>Saved content:</strong> analyses, lender comparisons, and coaching threads you choose to save.
              </li>
              <li>
                <strong>Payment information:</strong> billing details are handled by our payment processor; we do not store full card numbers.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">2. How We Use Your Information</h2>
            <p className="mt-2 text-muted-foreground">
              We use your information to provide and improve LendyTrack, personalize your experience, process payments,
              communicate with you, detect fraud and abuse, and comply with legal obligations.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">3. Cookies and Analytics</h2>
            <p className="mt-2 text-muted-foreground">
              We use essential cookies to keep you signed in and maintain your session. We may also use analytics cookies
              to understand how the service is used. You can control cookies through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">4. Data Sharing and Subprocessors</h2>
            <p className="mt-2 text-muted-foreground">
              We do not sell your personal data. We share information only with service providers necessary to operate the platform,
              such as our cloud database and authentication provider and our payment processor.
              These providers are contractually bound to protect your data.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">5. Data Retention and Deletion</h2>
            <p className="mt-2 text-muted-foreground">
              We keep your data for as long as your account is active or as needed to provide the service.
              You can request deletion of your account and associated data by contacting support.
              Some information may be retained as required by law.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">6. Your Privacy Choices</h2>
            <p className="mt-2 text-muted-foreground">
              You can update your account information, manage subscription settings, and request a copy or deletion of your data
              by emailing{" "}
              <a href="mailto:support@lendytrack.com" className="text-primary hover:underline">support@lendytrack.com</a>.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">7. Security</h2>
            <p className="mt-2 text-muted-foreground">
              We use encryption in transit, access controls, and row-level security to protect your data.
              No system is completely secure, and we encourage you to use a strong, unique password.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">8. Children's Privacy</h2>
            <p className="mt-2 text-muted-foreground">
              LendyTrack is not intended for children under 13. We do not knowingly collect personal data from children under 13.
              If you believe we have collected such data, please contact us so we can delete it.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">9. Changes to This Policy</h2>
            <p className="mt-2 text-muted-foreground">
              We may update this Privacy Policy as the service evolves. We will post the revised policy in the app and update the effective date.
              Continued use after changes means you accept the updated policy.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold">10. Contact Us</h2>
            <p className="mt-2 text-muted-foreground">
              Oasis Media Group LLC
              <br />
              <a href="mailto:support@lendytrack.com" className="text-primary hover:underline">support@lendytrack.com</a>
            </p>
          </section>

          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} LendyTrack. All rights reserved. An Oasis Media Group LLC Company.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
